import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Copy, DollarSign, Save, Settings, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import { settingsApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ErrorState,
  Field,
  PageHeader,
  PageShell,
  TextField,
  apiMessage,
  glassPanel,
  glassSubtle,
  pillButton,
} from '@/pages/glass';

const LIMITS = {
  heartbeat_timeout_seconds: { min: 30, max: 86400, label: 'Heartbeat timeout' },
  offline_check_interval_seconds: { min: 10, max: 3600, label: 'Offline check interval' },
};

const emptySettings = {
  heartbeat_timeout_seconds: '',
  default_currency: 'AED',
  offline_check_interval_seconds: '',
};

const wholeNumberError = (value, { min, max, label }) => {
  if (value === '' || value === null || value === undefined) return `Enter a ${label.toLowerCase()}.`;
  const number = Number(value);
  if (!Number.isInteger(number)) return `${label} must be a whole number of seconds.`;
  if (number < min || number > max) return `${label} must be between ${min} and ${max} seconds.`;
  return '';
};

const validateSettings = (settings) => {
  const errors = {};
  const heartbeat = wholeNumberError(settings.heartbeat_timeout_seconds, LIMITS.heartbeat_timeout_seconds);
  const interval = wholeNumberError(settings.offline_check_interval_seconds, LIMITS.offline_check_interval_seconds);
  if (heartbeat) errors.heartbeat_timeout_seconds = heartbeat;
  if (interval) errors.offline_check_interval_seconds = interval;
  return errors;
};

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [settings, setSettings] = useState(emptySettings);
  const [saved, setSaved] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await settingsApi.get();
      const next = {
        heartbeat_timeout_seconds: response.data?.heartbeat_timeout_seconds ?? 120,
        default_currency: 'AED',
        offline_check_interval_seconds: response.data?.offline_check_interval_seconds ?? 60,
      };
      setSettings(next);
      setSaved(next);
      setAttempted(false);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setLoadError(apiMessage(error, 'Failed to load settings. Nothing was changed.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const errors = attempted ? validateSettings(settings) : {};
  const dirty = saved
    && (
      String(settings.heartbeat_timeout_seconds) !== String(saved.heartbeat_timeout_seconds)
      || String(settings.offline_check_interval_seconds) !== String(saved.offline_check_interval_seconds)
    );

  const updateField = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async (event) => {
    event?.preventDefault();
    setAttempted(true);
    const nextErrors = validateSettings(settings);
    if (Object.keys(nextErrors).length) return;
    if (!dirty) {
      toast.message('No changes to save');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        heartbeat_timeout_seconds: Number(settings.heartbeat_timeout_seconds),
        offline_check_interval_seconds: Number(settings.offline_check_interval_seconds),
        default_currency: 'AED',
      };
      await settingsApi.update(payload);
      setSettings(payload);
      setSaved(payload);
      setAttempted(false);
      toast.success('Settings saved');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(apiMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const copyEndpoint = async () => {
    const endpoint = `wss://${window.location.host}/ocpp/{CHARGE_POINT_ID}`;
    try {
      await navigator.clipboard.writeText(endpoint);
      toast.success('WebSocket endpoint copied');
    } catch (error) {
      toast.error('Could not copy the endpoint');
    }
  };

  if (loading) {
    return (
      <PageShell testId="settings-loading" aria-busy="true">
        <span className="sr-only">Loading settings</span>
        <div className="h-8 w-48 animate-pulse rounded-full bg-muted" />
        <div className={cn(glassPanel, 'h-96 animate-pulse')} />
      </PageShell>
    );
  }

  if (loadError) {
    return (
      <PageShell testId="settings-page">
        <PageHeader eyebrow="System" title="Settings" description="Configure connection timing and review how chargers reach Vdrive." />
        <ErrorState message={loadError} onRetry={fetchSettings} />
      </PageShell>
    );
  }

  const endpoint = `wss://${window.location.host}/ocpp/{CHARGE_POINT_ID}`;

  return (
    <PageShell testId="settings-page">
      <PageHeader
        eyebrow="System"
        title="Settings"
        description="Configure connection timing and review how chargers reach Vdrive."
        actions={(
          <Button type="submit" form="settings-form" className={pillButton} disabled={saving} data-testid="save-settings-btn">
            <Save className="h-4 w-4" aria-hidden="true" />
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        )}
      />

      {dirty ? (
        <p className={cn(glassSubtle, 'px-4 py-2 text-sm')} role="status">You have unsaved changes.</p>
      ) : null}

      <form id="settings-form" className="grid gap-6" onSubmit={handleSave} noValidate>
        <Card className={glassPanel}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wifi className="h-5 w-5" aria-hidden="true" />
              Connection settings
            </CardTitle>
            <CardDescription>How long Vdrive waits before treating a charger as offline.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="heartbeat-timeout"
              label="Heartbeat timeout (seconds)"
              type="number"
              inputMode="numeric"
              required
              min={LIMITS.heartbeat_timeout_seconds.min}
              max={LIMITS.heartbeat_timeout_seconds.max}
              step="1"
              value={settings.heartbeat_timeout_seconds}
              error={errors.heartbeat_timeout_seconds}
              hint="A charger is offline when no heartbeat arrives within this window."
              data-testid="heartbeat-timeout"
              onChange={(event) => updateField('heartbeat_timeout_seconds', event.target.value)}
            />
            <TextField
              id="offline-check"
              label="Offline check interval (seconds)"
              type="number"
              inputMode="numeric"
              required
              min={LIMITS.offline_check_interval_seconds.min}
              max={LIMITS.offline_check_interval_seconds.max}
              step="1"
              value={settings.offline_check_interval_seconds}
              error={errors.offline_check_interval_seconds}
              hint="How often Vdrive scans for chargers that missed their heartbeat."
              data-testid="offline-check"
              onChange={(event) => updateField('offline_check_interval_seconds', event.target.value)}
            />
          </CardContent>
        </Card>

        <Card className={glassPanel}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" aria-hidden="true" />
              Currency
            </CardTitle>
            <CardDescription>New tariff plans use this currency.</CardDescription>
          </CardHeader>
          <CardContent>
            <Field id="currency-fixed" label="Default currency" hint="Currency is fixed to UAE Dirham for this deployment.">
              <div
                id="currency-fixed"
                className={cn(glassSubtle, 'flex h-10 items-center px-3 text-sm font-medium')}
                data-testid="currency-fixed"
              >
                AED — UAE Dirham
              </div>
            </Field>
          </CardContent>
        </Card>

        <Card className={glassPanel}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" aria-hidden="true" />
              OCPP connection
            </CardTitle>
            <CardDescription>Point each charger at this central system.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={cn(glassSubtle, 'p-4')}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">WebSocket endpoint</p>
                  <code className="mt-1 block break-all font-mono text-sm">{endpoint}</code>
                </div>
                <Button type="button" variant="outline" size="sm" className={pillButton} onClick={copyEndpoint}>
                  <Copy className="h-4 w-4" aria-hidden="true" />
                  Copy
                </Button>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className={cn(glassSubtle, 'p-4')}>
                <p className="text-xs text-muted-foreground">Protocol</p>
                <p className="mt-1 font-mono text-sm">OCPP 1.6J (JSON over WebSocket)</p>
              </div>
              <div className={cn(glassSubtle, 'p-4')}>
                <p className="text-xs text-muted-foreground">Subprotocol</p>
                <p className="mt-1 font-mono text-sm">ocpp1.6</p>
              </div>
            </div>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              <li>Set the charger central system URL to the WebSocket endpoint.</li>
              <li>Replace {'{CHARGE_POINT_ID}'} with that charger’s unique identifier.</li>
              <li>The charger shows up once it connects and sends a boot notification.</li>
            </ol>
          </CardContent>
        </Card>

        <Card className={glassPanel}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" aria-hidden="true" />
              Simulator
            </CardTitle>
            <CardDescription>Exercise the connection without a physical charger.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className={cn(glassSubtle, 'overflow-x-auto p-4')}>
              <p className="text-xs text-muted-foreground">Run simulator</p>
              <code className="mt-1 block whitespace-nowrap font-mono text-sm">
                python simulator.py --url ws://localhost:8001/ocpp/CHARGER001 --demo
              </code>
            </div>
            <p className="text-sm text-muted-foreground">
              The simulator connects as a charger, sends a boot notification and periodic heartbeats, and can run a charging session with meter values.
            </p>
            <div className="flex justify-end">
              <Button type="submit" className={pillButton} disabled={saving}>
                <Save className="h-4 w-4" aria-hidden="true" />
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </PageShell>
  );
};

export default SettingsPage;
